import React, { useContext, useMemo } from 'react';
import FilterGroup from "../Filters/FilterGroup";
import { Grid, Box, Typography } from '@mui/material';
import { WindowSizeContext } from '../../../../common/context/WindowSizeContext';
import TableComponentV2 from '../../../../common/components/TableComponentV2';
import LoadingComponent from '../../../../common/components/LoadingComponent';
import { useSalesPerformanceData } from '../hooks';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { convertToValidNumber, sortArrayBasedOnGivenArray } from '../../../../common/utils';
import BarIndicator from '../../common/presentation/barIndicator';
import NumericValueDisplay from '../../common/presentation/numericValueDisplay';
import ToggleSelector from '../../common/presentation/selectors/toggleSelector';
import { PERIOD_OPTIONS } from '../constants';
import { useIsComponentUsable } from '../../common/providers/blacklistedComponentsProvider/hooks';
import { 
    COMPONENT_PERFORMANCE_PER_GROUP_LABEL,
    COMPONENT_PERFORMANCE_PER_GROUP_SALES,
    COMPONENT_PERFORMANCE_PER_GROUP_CONTRIBUTION,
    COMPONENT_PERFORMANCE_PER_GROUP_TARGET_SALES,
    COMPONENT_PERFORMANCE_PER_GROUP_VS_TARGET,
    COMPONENT_PERFORMANCE_PER_GROUP_LY_SALES,
    COMPONENT_PERFORMANCE_PER_GROUP_VS_LY_SALES,
} from '../dashboardDefinition';

const IndicatorCellBox = (props) => (
    <Box sx={{ my: 1, width: '3rem', height: '1.5rem', marginX: 'auto', display: 'flex', justifyContent: 'center' }} >
        {props.children}
    </Box>
);

const chooseGroup = {
    'mall_type': 'mallType',
    'store_size': 'storeSize',
    'mall_category': 'mallCategory',
    'region': 'region',
    'product_group': 'productGroup'
}

export default function SalesPerformancePerGroup({ params, filters, onDropDownFilterChange, selectedRows, onSelectRow,
    period, onToggleChange }) {

    const columnVisibility = {
        'group': useIsComponentUsable(COMPONENT_PERFORMANCE_PER_GROUP_LABEL),
        'actual_sales': useIsComponentUsable(COMPONENT_PERFORMANCE_PER_GROUP_SALES),
        'percent_contribution': useIsComponentUsable(COMPONENT_PERFORMANCE_PER_GROUP_CONTRIBUTION),
        'target_sales': useIsComponentUsable(COMPONENT_PERFORMANCE_PER_GROUP_TARGET_SALES),
        'vs_target': useIsComponentUsable(COMPONENT_PERFORMANCE_PER_GROUP_VS_TARGET),
        'ly_sales': useIsComponentUsable(COMPONENT_PERFORMANCE_PER_GROUP_LY_SALES),
        'vs_ly_sales': useIsComponentUsable(COMPONENT_PERFORMANCE_PER_GROUP_VS_LY_SALES),
    }

    let absoluteHeaderLabel = '';
    let orderArray;
    const windowSize = useContext(WindowSizeContext);
    switch (chooseGroup[filters?.group]) {
        case 'productGroup':
            orderArray = ['Fresh', 'Food1', 'Food2', 'Nonfood', 'Drygoods', 'MBU'];
            absoluteHeaderLabel = "Category"
            break;
        case 'region':
            orderArray = ['NCR', 'NORTH LUZON', 'SOUTH LUZON', 'VISAYAS', 'NORTH EAST', 'MINDANAO'];
            absoluteHeaderLabel = "Region"
            break;
        case 'storeSize':
            orderArray = ['EXTRA SMALL', 'SMALL', 'MEDIUM', 'LARGE'];
            absoluteHeaderLabel = "Store Size"
            break;
        case 'mallType':
            absoluteHeaderLabel = "Store Type"
            break;
        case 'mallCategory':
            absoluteHeaderLabel = "Store Subtype"
            break;
        default:
            orderArray = [];
            break
    }

    const isMTD = period === 'mtd';
    const paramsBasedOnPeriod = isMTD ? params : { ...params, month: [1, ...(params?.month || [])] }
    const { data: performancePerGroupData, isLoading: isLoadingPerformancePerGroup } = useSalesPerformanceData({
        params: {
            ...paramsBasedOnPeriod,
            group_by: filters?.group
        }
    });

    const filteredperformancePerGroupData= performancePerGroupData?.filter(val => val.product_group !== 'Others' && val.product_group !== 'others')

    const isProductGroup = filters?.group === 'product_group';
    const PRODUCT_CAT_COLS = ['888', '888 (Outright)', '4k']

    const performance_per_group_columns = useMemo(
        () => [
            {
                id: 'group',
                header: absoluteHeaderLabel,
                accessorKey: filters?.group,
                cell: (row) => <Box sx={{ whiteSpace: 'nowrap', display: 'flex', alignItems: 'center' }}>
                    <VisibilityIcon sx={{ marginRight: '.5rem' }} fontSize='small' color={selectedRows?.includes(row?.row?.original?.[filters?.group]) ? 'primary' : 'disabled'} />
                    {isProductGroup && PRODUCT_CAT_COLS.includes(row?.getValue()) ? 'Top ' + row?.getValue() : row?.getValue()}
                </Box>,
                customStyle: { width: '15rem' }
            },
            {
                id: 'actual_sales',
                header: isMTD ? 'MTD' : 'YTD',
                accessorKey: 'actual_sales',
                cell: (row) => <Box sx={{ width: { sm: '50%', xs: '4rem' }, marginX: 'auto' }}> <NumericValueDisplay value={row?.getValue()} prefix={'₱'} isAbbreviated={windowSize?.isOnMobile} /></Box>,
                customStyle: { width: '15rem', textAlign: 'left', verticalAlign: 'middle' }
            },
            {
                id: 'percent_contribution',
                header: '% cont.',
                accessorKey: 'percent_contribution',
                cell: (row) => <Box sx={{ width: { sm: '50%' }, marginX: 'auto' }}>
                    <NumericValueDisplay value={row?.getValue()} postfix={'%'} />
                </Box>,
                customStyle: { width: '7rem', textAlign: 'center', verticalAlign: 'middle' }
            },
            {
                id: 'target_sales',
                header: isMTD ? 'MTD Target' : 'YTD Target',
                accessorKey: 'target_sales',
                cell: (row) => <Box sx={{ width: { sm: '50%', xs: '4rem' }, marginX: 'auto' }}>
                    <NumericValueDisplay value={row?.getValue()} prefix={'₱'} isAbbreviated={windowSize?.isOnMobile} />
                </Box>,
                customStyle: { width: '15rem', textAlign: 'left', verticalAlign: 'middle' }
            },
            {
                id: 'vs_target',
                header: 'vs Running Daily Target',
                accessorFn: (row) => (row?.actual_sales / row?.daily_target_sales) * 100,
                cell: (row) => {
                    if (row.row.original.daily_target_sales === undefined) {
                        return '-'
                    }
                    return <IndicatorCellBox>
                        <BarIndicator
                            colorFunctionFor={'sales'}
                            value={convertToValidNumber(row?.getValue())?.toFixed(1)}
                            isInverted={false} isValueColorCoded={true} isOnTable={true} label="vs last year" />
                    </IndicatorCellBox>
                },
                customStyle: { width: '15rem', textAlign: 'center', verticalAlign: 'middle' }
            },
            {
                id: 'ly_sales',
                header: isMTD ? 'Last Year MTD' : 'Last Year YTD',
                accessorKey: 'last_year_sales',
                cell: (row) => <Box sx={{ width: { sm: '50%', xs: '4rem' }, marginX: 'auto' }}> <NumericValueDisplay value={row?.getValue()} prefix={'₱'} isAbbreviated={windowSize?.isOnMobile} /> </Box>,
                customStyle: { width: '15rem', textAlign: 'left', verticalAlign: 'middle' }
            },
            {
                id: 'vs_ly_sales',
                header: 'vs Last Year',
                accessorFn: (row) => (row?.actual_sales / row?.last_year_sales) * 100,
                cell: (row) => <IndicatorCellBox>
                    <BarIndicator colorFunctionFor={'sales'} value={convertToValidNumber(row?.getValue())?.toFixed(1)} isInverted={false} isValueColorCoded={true} isOnTable={true} label="vs last year" />
                </IndicatorCellBox>,
                customStyle: { width: '15rem' }
            },
        ],
        [filters?.group, isProductGroup, selectedRows, period, columnVisibility]
    )

    const TopSalesTableData = isProductGroup && filteredperformancePerGroupData?.filter(val => PRODUCT_CAT_COLS.includes(val['product_group'])) // 888 and 4k data
    const filteredData = isProductGroup ? filteredperformancePerGroupData?.filter(val => !PRODUCT_CAT_COLS.includes(val['product_group'])) : filteredperformancePerGroupData // remove 888 and 4k when on product group
    const noNullRowData = filteredData?.filter(val => val[filters?.group] !== 'null' && val[filters?.group] !== null)
    const newOrderArray = isProductGroup ? ['888', 'Top 888', '888 (Outright)', 'Top 888 (Outright)' ,'4k', 'Top 4k', ...orderArray] : orderArray

    if (isLoadingPerformancePerGroup) {
        return <LoadingComponent />
    }

    const hasTopSalesTableData = !!TopSalesTableData && TopSalesTableData?.length !== 0;

    return (
        <>
            <Box flexGrow={1} xs={12} sx={{ backgroundColor: "#f6f6f8", borderRadius: ".5rem .5rem 0 0" }}>
                <Grid container alignItems="center" py={.5}>
                    {
                        windowSize.isOnMobile ? <>
                            <Grid item xs sm={2.5}>
                                <Typography variant="h6" ml={2.5}>
                                    Performance per
                                </Typography>
                            </Grid>
                            <Grid item xs display="flex" justifyContent={'end'} paddingY={windowSize?.isOnMobile && .75}>
                                <ToggleSelector
                                    options={PERIOD_OPTIONS}
                                    value={period}
                                    onChange={onToggleChange}/>
                            </Grid>
                            <FilterGroup
                                onDropdownChange={onDropDownFilterChange}
                                filters={filters} />
                        </> : <>
                            <FilterGroup
                                onDropdownChange={onDropDownFilterChange}
                                filters={filters} />
                            <Grid item sm={3} xs={12} display="flex" justifyContent={'end'} paddingY={windowSize?.isOnMobile && .75}>
                                <ToggleSelector
                                    options={PERIOD_OPTIONS}
                                    value={period}
                                    onChange={onToggleChange}/>
                            </Grid>
                        </>
                    }
                </Grid>
            </Box>
            <Grid container>
                <Grid item xs={12} sm={12} mt={0}>
                    {
                        isProductGroup && hasTopSalesTableData && <TableComponentV2
                            columns={performance_per_group_columns}
                            data={sortArrayBasedOnGivenArray(TopSalesTableData, newOrderArray, filters?.group)}
                            tableId={'salesPerformancePerGroupTopTable'}
                            enableRowSelection={true}
                            onSelectRow={onSelectRow}
                            isCompact={true}
                            customTableHeaderStyle={{
                                backgroundColor: '#caddeb'
                            }}
                            customTableContainerStyle={{
                                borderRadius: "0",
                                boxShadow: 'none',
                            }} 
                            initialState={{ columnVisibility }}
                        />
                    }
                </Grid>
                <Grid item xs={12} sm={12} mt={isProductGroup ? .75 : 0}>
                    <TableComponentV2
                        columns={performance_per_group_columns}
                        data={sortArrayBasedOnGivenArray(noNullRowData, newOrderArray, filters?.group)}
                        tableId={'salesPerformancePerGroupTable'}
                        enableRowSelection={true}
                        onSelectRow={onSelectRow}
                        isCompact={true}
                        customTableHeaderStyle={{
                            backgroundColor: '#caddeb'
                        }} 
                        initialState={{ columnVisibility }}
                    />
                </Grid>
            </Grid>
        </>
    );
}
